declare global {
  interface Window {
    google: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope?: string;
            callback: (resp: {
              access_token?: string;
              error?: string;
              expires_in?: number;
              scope?: string;
            }) => void;
          }) => {
            requestAccessToken: (config?: {
              scope?: string;
              prompt?: string;
            }) => void;
          };
        };
      };
    };
  }
}

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
const GOOGLE_TOKEN_SILENT_TIMEOUT_MS = 12000;
const GOOGLE_TOKEN_INTERACTIVE_TIMEOUT_MS = 180000;

let gisLoaded = false;
let lastAccessToken: string | null = null;
const grantedScopes = new Set<string>();

export const GOOGLE_SCOPES = {
  driveFile: 'https://www.googleapis.com/auth/drive.file',
  driveReadonly: 'https://www.googleapis.com/auth/drive.readonly',
  userInfoEmail: 'https://www.googleapis.com/auth/userinfo.email',
  calendarAppCreated: 'https://www.googleapis.com/auth/calendar.app.created',
} as const;

function normalizeScopes(scopes: string | string[]) {
  return Array.from(new Set((Array.isArray(scopes) ? scopes : scopes.split(' ')).filter(Boolean)));
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

async function ensureGis(): Promise<void> {
  if (gisLoaded) return;
  await loadScript('https://accounts.google.com/gsi/client');
  gisLoaded = true;
}

export function isGoogleConfigured(): boolean {
  return Boolean(CLIENT_ID);
}

async function requestGoogleAccessTokenAttempt(
  requestedScopeString: string,
  requestedScopes: string[],
  prompt: string,
  timeoutMs: number,
): Promise<string> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error('Google sign-in timed out.'));
    }, timeoutMs);

    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope: requestedScopeString,
      callback: (resp) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);

        if (resp.error) {
          reject(new Error(resp.error));
          return;
        }

        if (!resp.access_token) {
          reject(new Error('No Google access token was returned.'));
          return;
        }

        lastAccessToken = resp.access_token;
        requestedScopes.forEach((scope) => grantedScopes.add(scope));
        resolve(resp.access_token);
      },
    });

    client.requestAccessToken({
      scope: requestedScopeString,
      prompt,
    });
  });
}

export async function requestGoogleAccessToken(scopes: string | string[]): Promise<string> {
  if (!CLIENT_ID) {
    throw new Error('VITE_GOOGLE_CLIENT_ID is not set.');
  }

  await ensureGis();

  const requestedScopes = normalizeScopes(scopes);
  const requestedScopeString = requestedScopes.join(' ');
  const canTrySilentReuse = requestedScopes.every((scope) => grantedScopes.has(scope)) && Boolean(lastAccessToken);

  if (!canTrySilentReuse) {
    return requestGoogleAccessTokenAttempt(
      requestedScopeString,
      requestedScopes,
      'consent',
      GOOGLE_TOKEN_INTERACTIVE_TIMEOUT_MS,
    );
  }

  try {
    return await requestGoogleAccessTokenAttempt(
      requestedScopeString,
      requestedScopes,
      '',
      GOOGLE_TOKEN_SILENT_TIMEOUT_MS,
    );
  } catch (error) {
    const message = error instanceof Error ? error.message : '';
    if (message !== 'Google sign-in timed out.') {
      throw error;
    }

    return requestGoogleAccessTokenAttempt(
      requestedScopeString,
      requestedScopes,
      'consent',
      GOOGLE_TOKEN_INTERACTIVE_TIMEOUT_MS,
    );
  }
}

export async function fetchGoogleUserEmail(accessToken: string): Promise<string | null> {
  const response = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    return null;
  }

  const data = await response.json();
  return typeof data.email === 'string' ? data.email : null;
}
